"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExchangeChallengeSchema = exports.ExchangeOrderSchema = exports.ExchangeRecipientRequestSchema = exports.CreateExchangeOrderRequestSchema = exports.ExchangeDirectionsResponseSchema = exports.ExchangeDirectionSchema = exports.EXCHANGE_RECIPIENT_FIELDS = exports.DecimalStringSchema = exports.TERMINAL_EXCHANGE_STATUSES = exports.ExchangeStatusSchema = void 0;
const zod_1 = require("zod");
const wallet_1 = require("./wallet");
exports.ExchangeStatusSchema = zod_1.z.enum([
    'created',
    'ready',
    'awaiting_confirmation',
    'paying',
    'paid',
    'processing',
    'completed',
    'expired',
    'payment_failed',
    'failed',
]);
exports.TERMINAL_EXCHANGE_STATUSES = [
    'completed',
    'expired',
    'payment_failed',
    'failed',
];
/** `"43.3570"`: destination units per one unit of the source, as the exchanger quotes it. */
exports.DecimalStringSchema = zod_1.z.string().regex(/^\d+(\.\d+)?$/);
/** The recipient fields the exchanger may require; anything else it lists is ignored by CCex. */
exports.EXCHANGE_RECIPIENT_FIELDS = [
    'toWallet',
    'firstName',
    'lastName',
    'phone',
    'bankId',
    'customerAddress',
];
exports.ExchangeDirectionSchema = zod_1.z.object({
    /** The exchanger's currency code, e.g. `P24UAH`. */
    toCurrencyType: zod_1.z.string(),
    name: zod_1.z.string(),
    alpha3: zod_1.z.string(),
    group: zod_1.z.string(),
    sort: zod_1.z.number().int(),
    rate: exports.DecimalStringSchema,
    /** Atomic units of the source asset. */
    minFrom: wallet_1.AtomicAmountSchema,
    maxFrom: wallet_1.AtomicAmountSchema,
    /** Minor units of the destination currency. */
    minTo: wallet_1.AtomicAmountSchema,
    maxTo: wallet_1.AtomicAmountSchema,
    toDecimals: zod_1.z.number().int().min(0),
    /** Only ids from EXCHANGE_RECIPIENT_FIELDS: the API drops anything else the exchanger lists (apps/api/src/exchange/directions.ts, `KNOWN_FIELDS`) before this schema ever sees it. */
    requiredFields: zod_1.z.array(zod_1.z.enum(exports.EXCHANGE_RECIPIENT_FIELDS)),
    /**
     * The exchanger's own wording for a required field, when it sends one; the Mini App shows it
     * instead of the catalogue label. Cash destinations use `toWallet` for a Telegram contact,
     * which no catalogue key could guess.
     */
    fieldLabels: zod_1.z.partialRecord(zod_1.z.enum(exports.EXCHANGE_RECIPIENT_FIELDS), zod_1.z.string()),
    /** False when the exchanger reports no reserve for the destination. */
    available: zod_1.z.boolean(),
});
exports.ExchangeDirectionsResponseSchema = zod_1.z.object({
    /** `exchange.enabled`; false comes with an empty `assets` list, so Home can hide the button without a second call. */
    enabled: zod_1.z.boolean(),
    updatedAt: zod_1.z.iso.datetime(),
    assets: zod_1.z.array(zod_1.z.object({ assetCode: zod_1.z.string(), directions: zod_1.z.array(exports.ExchangeDirectionSchema) })),
});
exports.CreateExchangeOrderRequestSchema = zod_1.z.object({
    assetCode: zod_1.z.string().min(1),
    toCurrencyType: zod_1.z.string().min(1).max(32),
    amountSold: wallet_1.AtomicAmountSchema.refine((value) => /[1-9]/.test(value), {
        message: 'amount must be greater than zero',
    }),
});
exports.ExchangeRecipientRequestSchema = zod_1.z
    .object({
    /** Card number or IBAN, spaces allowed; the exchanger validates it. */
    toWallet: zod_1.z.string().trim().min(4).max(64),
    firstName: zod_1.z.string().trim().min(1).max(64).optional(),
    lastName: zod_1.z.string().trim().min(1).max(64).optional(),
    phone: zod_1.z.string().trim().min(5).max(32).optional(),
    bankId: zod_1.z.string().trim().min(1).max(64).optional(),
    customerAddress: zod_1.z.string().trim().min(1).max(200).optional(),
})
    .strict();
exports.ExchangeOrderSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    status: exports.ExchangeStatusSchema,
    assetCode: zod_1.z.string(),
    symbol: zod_1.z.string(),
    decimals: zod_1.z.number().int().min(0),
    chainCode: zod_1.z.string(),
    chainName: zod_1.z.string(),
    fromAmount: wallet_1.AtomicAmountSchema,
    toAmount: wallet_1.AtomicAmountSchema,
    toDecimals: zod_1.z.number().int().min(0),
    toAlpha3: zod_1.z.string(),
    toCurrencyType: zod_1.z.string(),
    toCurrencyName: zod_1.z.string(),
    /** The exchanger's destination group (`CARD`, `FIAT`, `CASH`, ...); null on orders created before it was stored. Drives payout wording. */
    toGroup: zod_1.z.string().nullable(),
    rate: exports.DecimalStringSchema,
    /** `•••• 1111` for a card or account; the whole contact for a `CASH` destination; null before the recipient step. */
    recipientMasked: zod_1.z.string().nullable(),
    exchangerOrderId: zod_1.z.number().int(),
    supportEmail: zod_1.z.string().nullable(),
    withdrawalId: zod_1.z.uuid().nullable(),
    txHash: zod_1.z.string().nullable(),
    explorerUrl: zod_1.z.url().nullable(),
    createdAt: zod_1.z.iso.datetime(),
    /** When the transfer left the wallet: the withdrawal's broadcast time, or its code confirmation if not broadcast yet. */
    sentAt: zod_1.z.iso.datetime().nullable(),
    paidAt: zod_1.z.iso.datetime().nullable(),
    /** When the exchanger first reported our transfer as received. */
    processingAt: zod_1.z.iso.datetime().nullable(),
    completedAt: zod_1.z.iso.datetime().nullable(),
    errorCode: zod_1.z.string().nullable(),
});
/** What `POST /exchange/orders/:id/pay` returns: the withdrawal's code challenge. */
exports.ExchangeChallengeSchema = zod_1.z.object({
    withdrawalId: zod_1.z.uuid(),
    emailMasked: zod_1.z.string(),
    expiresAt: zod_1.z.iso.datetime(),
    resendAvailableAt: zod_1.z.iso.datetime(),
});
//# sourceMappingURL=exchange.js.map